import {
  getInvestmentEntityStatsRetrieveLoader,
  getInvestmentTableDataRetrieveLoader,
} from "gen/endpoints/investment/investment";
import i18n from "i18n/config";
import { RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const investmentRoutes: RouteObject = {
  id: "fundFamilyInvestments",
  path: "investments",
  children: [
    {
      id: "investmentIndex",
      index: true,
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { InvestmentsPage } = await import(
          "features/investments/InvestmentsPage"
        );
        return { Component: InvestmentsPage };
      },
      loader: composeLoaders(
        getInvestmentTableDataRetrieveLoader(),
        getInvestmentEntityStatsRetrieveLoader(),
      ),
    },
    {
      id: "investment",
      path: ":investmentId/*",
      lazy: async () => {
        const { InvestmentSetupPage } = await import(
          "features/investments/setup/InvestmentSetupPage"
        );
        return { Component: InvestmentSetupPage };
      },
      children: [
        {
          id: "investmentActivity",
          path: "activity",
          lazy: async () => {
            const { InvestmentTabActivity } = await import(
              "features/investments/setup/InvestmentTabActivity"
            );
            return { Component: InvestmentTabActivity };
          },
          handle: {
            title: i18n.t("common:activity"),
          },
        },
        {
          id: "investmentSetupProfile",
          path: "",
          lazy: async () => {
            const { InvestmentSetupTabProfile } = await import(
              "features/investments/setup/InvestmentSetupTabProfile"
            );
            return { Component: InvestmentSetupTabProfile };
          },
          handle: {
            title: i18n.t("common:profile"),
          },
        },
      ],
    },
    {
      id: "fundFamilyInvestmentsCreate",
      path: "create",
      lazy: async () => {
        const { AddInvestmentPage } = await import(
          "features/investments/AddInvestmentPage"
        );
        return { Component: AddInvestmentPage };
      },
    },
  ],
};

/*
Maintaining a separate set of routes and components for portfolios for a couple reasons:
1. Allows for separate tab props that contain investmentId for investments and portfolioId for portfolios, which makes things a little bit clearer when looking at the code.
2. In the future, separate routes allow us to have multiple sets of routes for investments in portfolios: /fund-family/<fund-family-id>/investments/<investment-id> and /fund-family/<fund-family-id>/portfolio/<portfolio-id>/investments/<investment-id>
3. The Profile tabs for investments and portfolios are different enough that a shared component doesn't make sense.
4. Only portfolios have an Investments tab.
*/
export const portfolioRoutes: RouteObject = {
  id: "portfolios",
  path: "portfolios",
  children: [
    {
      id: "portfolio",
      path: ":portfolioId/*",
      lazy: async () => {
        const { PortfolioPage } = await import(
          "features/investments/PortfolioPage"
        );
        return { Component: PortfolioPage };
      },
      children: [
        {
          id: "portfolioActivity",
          path: "activity",
          lazy: async () => {
            const { PortfolioTabActivity } = await import(
              "features/investments/PortfolioTabActivity"
            );
            return { Component: PortfolioTabActivity };
          },
          handle: {
            title: i18n.t("common:activity"),
          },
        },
        {
          id: "portfolioProfile",
          path: "",
          lazy: async () => {
            const { PortfolioTabProfile } = await import(
              "features/investments/PortfolioTabProfile"
            );
            return { Component: PortfolioTabProfile };
          },
          handle: {
            title: i18n.t("common:profile"),
          },
        },
        {
          id: "portfolioInvestments",
          path: "investments",
          lazy: async () => {
            const { PortfolioTabInvestments } = await import(
              "features/investments/PortfolioTabInvestments"
            );
            return { Component: PortfolioTabInvestments };
          },
          handle: {
            title: i18n.t("investment:tabs.portfolioInvestments.title"),
          },
        },
      ],
    },
  ],
};
